import { Component, computed, effect, input, signal, ViewChild } from '@angular/core';
import { Client, ServiceProviderClient } from '../../models/client';
import { GoogleMap, MapMarker } from '@angular/google-maps';
import { Router } from '@angular/router';
import { MarkerClusterer, GridAlgorithm } from '@googlemaps/markerclusterer';
import { ServiceProviderService } from '../../service-provider/service-provider.service';
import { NotificationService } from '../../shared/notification.service';
import { IncidentLocation } from '../../models/incident';
import { AppModule } from "../../app.module";


@Component({
  selector: 'app-map-service-providers',
  imports: [GoogleMap, MapMarker, AppModule],
  templateUrl: './map-service-providers.html',
  styleUrl: './map-service-providers.less'
})
export class MapServiceProviders {
  
  @ViewChild(GoogleMap) map!: GoogleMap;

  serviceProviders = input<ServiceProviderClient[] >();

  incidentLocation = input<IncidentLocation | null>(null);

  public selectedServiceProvider = signal<ServiceProviderClient | null>(null);

  private markerClusterer: MarkerClusterer;
  private markers: google.maps.Marker[] = [];
  private pulseMarkers: google.maps.Marker[] = [];

  center: google.maps.LatLngLiteral = { lat: -29.1, lng: 28.2 }; 
  zoom = signal(6);
  mapOptions: google.maps.MapOptions = {
  disableDefaultUI: true, 
  clickableIcons: false,
  styles: [
    { elementType: "geometry", stylers: [{ color: "#1d2c4d" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#8ec3b9" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#1a3646" }] },
    {
      featureType: "administrative.country",
      elementType: "geometry.stroke",
      stylers: [{ color: "#4b6878" }],
    },
    {
      featureType: "administrative.land_parcel",
      elementType: "labels.text.fill",
      stylers: [{ color: "#64779e" }],
    },
    {
      featureType: "administrative.province",
      elementType: "geometry.stroke",
      stylers: [{ color: "#4b6878" }],
    },
    {
      featureType: "landscape.man_made",
      elementType: "geometry.stroke",
      stylers: [{ color: "#334e87" }],
    },
    {
      featureType: "landscape.natural",
      elementType: "geometry",
      stylers: [{ color: "#023e58" }],
    },
    {
      featureType: "poi",
      elementType: "geometry",
      stylers: [{ color: "#283d6a" }],
    },
    {
      featureType: "poi",
      elementType: "labels.text.fill",
      stylers: [{ color: "#6f9ba5" }],
    },
    {
      featureType: "poi",
      elementType: "labels.text.stroke",
      stylers: [{ color: "#1d2c4d" }],
    },
    {
      featureType: "poi.park",
      elementType: "geometry.fill",
      stylers: [{ color: "#023e58" }],
    },
    {
      featureType: "poi.park",
      elementType: "labels.text.fill",
      stylers: [{ color: "#3C7680" }],
    },
    {
      featureType: "road",
      elementType: "geometry",
      stylers: [{ color: "#304a7d" }],
    },
    {
      featureType: "road",
      elementType: "labels.text.fill",
      stylers: [{ color: "#98a5be" }],
    },
    {
      featureType: "road",
      elementType: "labels.text.stroke",
      stylers: [{ color: "#1d2c4d" }],
    },
    {
      featureType: "road.highway",
      elementType: "geometry",
      stylers: [{ color: "#2c6675" }],
    },
    {
      featureType: "road.highway",
      elementType: "geometry.stroke",
      stylers: [{ color: "#255763" }],
    },
    {
      featureType: "road.highway",
      elementType: "labels.text.fill",
      stylers: [{ color: "#b0d5ce" }],
    },
    {
      featureType: "road.highway",
      elementType: "labels.text.stroke",
      stylers: [{ color: "#023e58" }],
    },
    {
      featureType: "transit",
      elementType: "labels.text.fill",
      stylers: [{ color: "#98a5be" }],
    },
    {
      featureType: "transit",
      elementType: "labels.text.stroke",
      stylers: [{ color: "#1d2c4d" }],
    },
    {
      featureType: "transit.line",
      elementType: "geometry.fill",
      stylers: [{ color: "#283d6a" }],
    },
    {
      featureType: "transit.station",
      elementType: "geometry",
      stylers: [{ color: "#3a4762" }],
    },
    {
      featureType: "water",
      elementType: "geometry",
      stylers: [{ color: "#0e1626" }],
    },
    {
      featureType: "water",
      elementType: "labels.text.fill",
      stylers: [{ color: "#4e6d70" }],
    },
  ]
};

sortedServiceProviders = computed(() => {
  const sps = this.serviceProviders() || [];
  
  const available: ServiceProviderClient[] = [];
  const busy: ServiceProviderClient[] = [];
  const unavailable: ServiceProviderClient[] = [];
  const unknown: ServiceProviderClient[] = [];
  
  sps.forEach(sp => {
    const techs = sp.technicians || [];
    
    if (techs.some(tech => tech.statusId === 1)) {
      available.push(sp);
    } else if (techs.some(tech => tech.statusId === 2)) {
      unavailable.push(sp);
    } else if (techs.some(tech => tech.statusId === 3)) {
      busy.push(sp);
    } else {
      unknown.push(sp);
    }
  });
  
  return { available, busy, unavailable, unknown };
});

totalServiceProviders = computed(() => {
  return this.serviceProviders()?.length || 0;
});

availableServiceProviders = computed(() => {
  return this.sortedServiceProviders().available.length;
});

unavailableServiceProviders = computed(() => {
  return this.sortedServiceProviders().unavailable.length;
});

busyServiceProviders = computed(() => {
  return this.sortedServiceProviders().busy.length;
});

unknownStatusServiceProviders = computed(() => {
  return this.sortedServiceProviders().unknown.length;
});

availableTechsCount = computed(() => {
  let count = 0;
  this.selectedServiceProvider()?.technicians?.forEach(tech => {
    if (tech.statusId === 1) {
      count++;
    }
  });
  return count;
});

offlineTechsCount = computed(() => {
  let count = 0;
  this.selectedServiceProvider()?.technicians?.forEach(tech => {
    if (tech.statusId === 2 || tech.statusId === null) {
      count++;
    }
  });
  return count;
  });

busyTechsCount = computed(() => {
  let count = 0;
  this.selectedServiceProvider()?.technicians?.forEach(tech => {
    if (tech.statusId === 3) {
      count++;
    }});
  return count;
});



private mapReady = false;

constructor(public router: Router, private spService: ServiceProviderService, private notification: NotificationService) {
  effect(() => {
      const providers = this.serviceProviders();
      if (providers && this.mapReady) {
        this.updateMarkers();
      }
    });

    const animate = () => {
      if (this.pulseMarkers.length > 0) {
        this.animationFrame.set(Date.now());
        this.pulseMarkers.forEach((marker, index) => {
          const sp = this.serviceProviders()?.[index];
          marker.setIcon(this.getSPPulseIcon(sp));
        });
      }
      requestAnimationFrame(animate);
    };
    animate();
}


ngAfterViewInit(): void {
  console.log(this.serviceProviders())
    // Wait for the map to be fully loaded
    if (this.map.googleMap) {
      // Listen for the map to be fully loaded
      google.maps.event.addListenerOnce(this.map.googleMap, 'idle', () => {
        this.mapReady = true;
        this.initializeMarkerClusterer();
      });
      if (this.incidentLocation()) {
        this.center = { lat: this.incidentLocation()!.latitude, lng: this.incidentLocation()!.longitude };
        this.zoom.set(10);
        this.addIncidentLocationToMap();

      }
    }

  }

  private initializeMarkerClusterer(): void {
    // Create markers for all service providers
    this.createMarkers();

    // Initialize the clusterer
    this.markerClusterer = new MarkerClusterer({
      map: this.map.googleMap!,
      markers: this.markers,
      renderer: {
        render: ({ count, position, markers }) => {
          // Custom cluster marker styling
          const googleMarkers = markers;
          const color = this.getClusterColorByAvailability(googleMarkers as google.maps.Marker[]);
          const svg = `
            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
              <circle cx="20" cy="20" r="18" fill="${color}" stroke="white" stroke-width="3"/>
              <text x="20" y="25" font-family="Arial, sans-serif" font-size="12" font-weight="bold" 
                    fill="white" text-anchor="middle">${count}</text>
            </svg>
          `;
          
          return new google.maps.Marker({
            position,
            icon: {
              url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
              scaledSize: new google.maps.Size(40, 40),
              anchor: new google.maps.Point(20, 20)
            },
            zIndex: 1000
          });
        }
      }
    });
  }
private createMarkers(): void {
    this.markers = [];
    this.pulseMarkers.forEach(m => m.setMap(null));
    this.pulseMarkers = [];

    const providers = this.serviceProviders() || [];

    providers.forEach(sp => {
      const icon = this.getIconMarker(sp);

      const marker = new google.maps.Marker({
        position: { lat: sp.latitude, lng: sp.longitude },
        icon: icon,
        title: sp.clientName,
        zIndex: 2
      });

      (marker as any).serviceProvider = sp;

      // Add click listener to marker
      marker.addListener('click', () => {
        this.selectedServiceProvider.set(sp);
      });

      this.markers.push(marker);

      // ONLY add pulsing markers if we are in "Incident Mode" (have a location)
      if (this.incidentLocation() && this.map.googleMap) {
        const pulseMarker = new google.maps.Marker({
          position: { lat: sp.latitude, lng: sp.longitude },
          icon: this.getSPPulseIcon(sp),
          zIndex: 1, // Behind the main marker
          clickable: false, // Let clicks pass through to the main marker
          map: this.map.googleMap // Add DIRECTLY to map, not to clusterer
        });
        
        // Store explicit reference to SP on the marker to avoid fragile index lookups in animation loop
        (pulseMarker as any).serviceProvider = sp;
        
        this.pulseMarkers.push(pulseMarker);
      }
    });
  }


private getClusterColorByAvailability(markers: google.maps.Marker[]): string {
  let hasAvailable = false;
  let hasBusy = false;
  let hasUnavailable = false;
  let hasUnknown = false;

  // Analyze all service providers in this cluster
  markers.forEach(marker => {
    const sp = (marker as any).serviceProvider as ServiceProviderClient;
    if (sp && sp.technicians) {
      const techs = sp.technicians;
      
      if (techs.some(tech => tech.statusId === 1)) {
        hasAvailable = true;
      } else if (techs.some(tech => tech.statusId === 2)) {
        hasUnavailable = true;
      } else if (techs.some(tech => tech.statusId === 3)) {
        hasBusy = true;
      } else {
        hasUnknown = true;
      }
    }
  });

  // Priority order: Available > Busy > Unavailable > Unknown
  if (hasAvailable) {
    return '#4CAF50';     // Green - At least one available
  } else if (hasBusy) {
    return '#FF9800';     // Orange - Has busy but no available
  } else if (hasUnavailable) {
    return '#F44336';     // Red - Has unavailable but no available/busy
  } else {
    return '#9E9E9E';     // Gray - All unknown status
  }
}

  public updateMarkers(): void {
    // Call this method when serviceProviders input changes
    if (this.markerClusterer) {
      // Clear existing markers from clusterer
      this.markerClusterer.clearMarkers();
      
      // Clear existing pulse markers from the map
      this.pulseMarkers.forEach(m => m.setMap(null));
      this.pulseMarkers = [];

      // Create new markers
      this.createMarkers();
      
      // Add new markers to clusterer
      this.markerClusterer.addMarkers(this.markers);
    }
  }

public getIconMarker(sp: ServiceProviderClient): google.maps.Icon {
  const color = this.getMarkerColour(sp);
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
          <circle cx="16" cy="16" r="14" fill="${color}" stroke="white" stroke-width="2"/>
        </svg>
      `)}`,
      scaledSize: new google.maps.Size(24, 24),
      anchor: new google.maps.Point(12, 12)
    };
  }

  private getMarkerColour(sp: ServiceProviderClient): string {
    const techs = sp.technicians || [];
    
    if (techs.some(tech => tech.statusId === 1)) {
      return '#52c41a';
    } else if (techs.some(tech => tech.statusId === 2)) {
      return '#ff4d4f';
    } else if (techs.some(tech => tech.statusId === 3)) {
      return '#faad14';
    } else {
      return '#d9d9d9';
    }
  }

 public requestStatusUpdate(sp: ServiceProviderClient) {
    return this.spService.requestTechnicianStatusUpdate(sp.clientId).subscribe({
      next: (dt: Date) => {
        this.notification.showSuccess('Status update requested successfully.');
        // Update the last request time for this service provider
        this.selectedServiceProvider.update(sp => {
          if (sp) {
            sp.dtLastStatusRequest = dt;
          }
          return sp;
        });
      },
      error: (error) => {
        this.notification.handleError('Error requesting status update: ', error.message);
      }
    });
  }

  public addIncidentLocationToMap(): void {
    if (this.incidentLocation() && this.map.googleMap) {
      const incidentMarker = new google.maps.Marker({
        position: { lat: this.incidentLocation()!.latitude, lng: this.incidentLocation()!.longitude },
        icon: this.getIncidentIconMarker(),
        title: 'Breakdown Location',
        zIndex: 3
      });
      incidentMarker.setMap(this.map.googleMap);
      const incidentPulse = new google.maps.Marker({
        position: { lat: this.incidentLocation()!.latitude, lng: this.incidentLocation()!.longitude },
        icon: this.getPulseIcon(),
        title: 'Breakdown Location',
        zIndex: 2
      });
      incidentPulse.setMap(this.map.googleMap);
    }
  }

  public getIncidentIconMarker(): google.maps.Icon {
      return {
        url: '/icons/icon-72x72.png', 
        scaledSize: new google.maps.Size(32, 32),
        anchor: new google.maps.Point(16, 16)
      };
    }

    private animationFrame = signal(0);

  public getPulseIcon(): google.maps.Symbol {
      const time = this.animationFrame() / 250;
      
      return {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 20 + Math.sin(time) * 8,
        fillColor: '#bd0000',  
        fillOpacity: 0.2 + Math.sin(time) * 0.1, 
        strokeColor: '#bd0000',
        strokeWeight: 2,
        strokeOpacity: 0.4 + Math.sin(time) * 0.2
      };
    }

    public getSPPulseIcon(sp?: ServiceProviderClient): google.maps.Symbol {
      const time = this.animationFrame() / 250;
      const color = sp ? this.getMarkerColour(sp) : '#0036bd';
      
      return {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 20 + Math.sin(time) * 8,
        fillColor: color,  
        fillOpacity: 0.2 + Math.sin(time) * 0.1, 
        strokeColor: color,
        strokeWeight: 2,
        strokeOpacity: 0.4 + Math.sin(time) * 0.2
      };
    }
  public isStatusUpdateRequestAvailable(lastUpdateRequest: Date | null | undefined): boolean {
    if (!lastUpdateRequest) {
      return true; // No request made yet, so it's available
    }
    const now = new Date();
    const diffInMinutes = (now.getTime() - new Date(lastUpdateRequest + 'Z').getTime()) / (1000 * 60);
    return diffInMinutes >= 30; // Allow new request if it's been 30 minutes or more
  }
}
